import { LineBasicMaterial, MeshBasicMaterial, SphereGeometry } from 'three';
import { markRaw } from 'vue';
import { groupNode } from '@/lib/threeScene/nodes/group';
import type { LandmarkMap } from '@/domain/anatomy/hipSpineAngleOverlays';
import type { AngleNodes, AnnotationGroup, HipSpineAngleResult } from '@/views/3d-planner/hipSpine/types';
import { ANGLE_DISPLAY_CONFIG } from '@/views/3d-planner/hipSpine/types';
import { HIP_SPINE_ANGLE_COLOURS } from '@/views/3d-planner/hipSpine/hipSpineAnnotationDefs';
import { resolveLabelOverlaps } from '@/views/3d-planner/hipSpine/labelOverlapResolver';
import { buildLabelFromPlacement, type PendingAngleLabel } from '@/views/3d-planner/hipSpine/angles/angleLabels';
import { buildRayAngleNodes } from '@/views/3d-planner/hipSpine/angles/rayAngleNodes';
import { buildCurveAngleNodes } from '@/views/3d-planner/hipSpine/angles/curveAngleNodes';

const SPHERE_SEGMENTS = 32;
const RAY_LENGTH_FACTOR = 0.2;
const ARC_RADIUS_FACTOR = 0.05;
const SPHERE_RADIUS_FACTOR = 0.008;

type MaterialCache = {
    sharedGeometry: SphereGeometry;
    meshMaterial(colour: string): MeshBasicMaterial;
    lineMaterial(colour: string): LineBasicMaterial;
};

function createMaterialCache(sphereRadius: number): MaterialCache {
    const sharedGeometry = markRaw(new SphereGeometry(sphereRadius, SPHERE_SEGMENTS, SPHERE_SEGMENTS));
    const meshMaterials = new Map<string, MeshBasicMaterial>();
    const lineMaterials = new Map<string, LineBasicMaterial>();
    return {
        sharedGeometry,
        meshMaterial(colour) {
            if (!meshMaterials.has(colour)) {
                meshMaterials.set(colour, markRaw(new MeshBasicMaterial({ color: colour })));
            }
            return meshMaterials.get(colour)!;
        },
        lineMaterial(colour) {
            if (!lineMaterials.has(colour)) {
                lineMaterials.set(colour, markRaw(new LineBasicMaterial({ color: colour, depthTest: false })));
            }
            return lineMaterials.get(colour)!;
        },
    };
}

type AngleBuildShared = {
    cache: MaterialCache;
    landmarkMap: LandmarkMap;
    sphereRadius: number;
    arcRadius: number;
    dashLength: number;
    gapLength: number;
    maxDim: number;
    imageSize: { width: number; height: number };
};

function buildAngleNodes(
    angle: HipSpineAngleResult,
    colour: string,
    groupId: string,
    shared: AngleBuildShared,
): AngleNodes | null {
    const refs = angle.landmarkRefs;
    if (refs.kind === 'ray') {
        return buildRayAngleNodes(angle, refs, {
            groupId,
            landmarkMap: shared.landmarkMap,
            maxDim: shared.maxDim,
            imageSize: shared.imageSize,
            sharedGeometry: shared.cache.sharedGeometry,
            meshMaterial: shared.cache.meshMaterial(colour),
            lineMaterial: shared.cache.lineMaterial(colour),
            sphereRadius: shared.sphereRadius,
            arcRadius: shared.arcRadius,
            dashLength: shared.dashLength,
            gapLength: shared.gapLength,
            colour,
            config: ANGLE_DISPLAY_CONFIG[angle.id],
        });
    }
    return buildCurveAngleNodes(angle, refs, {
        groupId,
        landmarkMap: shared.landmarkMap,
        lineMaterial: shared.cache.lineMaterial(colour),
        sphereRadius: shared.sphereRadius,
        colour,
    });
}

/**
 * Build angle visualisation groups from computed angle results and landmark positions.
 * Returns plain AnnotationGroup[] without touching the scene.
 *
 * Geometry and materials are shared across angles of the same colour
 * to reduce GPU memory and draw-call overhead.
 */
export function buildAngleGroups(
    angles: HipSpineAngleResult[],
    landmarkMap: LandmarkMap,
    imageSize: { width: number; height: number },
): AnnotationGroup[] {
    const maxDim = Math.max(imageSize.width, imageSize.height);
    const sphereRadius = maxDim * SPHERE_RADIUS_FACTOR;
    const dashLength = maxDim * RAY_LENGTH_FACTOR * 0.15;
    const shared: AngleBuildShared = {
        cache: createMaterialCache(sphereRadius),
        landmarkMap,
        sphereRadius,
        arcRadius: maxDim * ARC_RADIUS_FACTOR,
        dashLength,
        gapLength: dashLength * 0.6,
        maxDim,
        imageSize,
    };

    const groups: AnnotationGroup[] = [];
    const pendingLabels: PendingAngleLabel[] = [];

    for (const angle of angles) {
        const colour = HIP_SPINE_ANGLE_COLOURS[angle.id];
        const groupId = `angle-${angle.id}`;
        const result = buildAngleNodes(angle, colour, groupId, shared);
        if (!result) continue;

        groups.push({
            label: angle.abbreviation,
            name: angle.displayName,
            category: angle.category,
            colour,
            sceneGroup: groupNode(groupId),
            children: result.children,
        });

        if (result.labelPlacement) {
            pendingLabels.push({ ...result.labelPlacement, groupIndex: groups.length - 1 });
        }
    }

    resolveLabelOverlaps(pendingLabels, sphereRadius);
    for (const pending of pendingLabels) {
        groups[pending.groupIndex].children.push(buildLabelFromPlacement(pending));
    }

    return groups;
}
